import React, { useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import LinkIcon from '@atlaskit/icon/core/migration/link';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';
import { usePublish } from '@atlaskit/rovo-triggers';

import { messages } from './messages';

const SUMMARIZE_IN_CHAT_CONTAINER_WIDTH_BREAKPOINT = 700;

interface SearchResultContextMenuProps {
	url?: string;
	hideSummarizeInChat?: boolean;
	isContextMenuOpen: boolean;
	containerWidth: number;
	setIsContextMenuOpen: (open: boolean) => void;
	setIsFocused: (open: boolean) => void;
	setIsHovered: (open: boolean) => void;
	id: string;
	cloudId?: string;
	onSummarizeInChatButtonClicked?: VoidFunction;
	/** Called when the context menu copy button is clicked */
	onContextMenuCopyButtonClicked?: VoidFunction;
	/** Called when the context menu button is clicked */
	onContextMenuButtonClicked?: VoidFunction;
}

const SearchResultContextMenu = ({
	url,
	hideSummarizeInChat = false,
	isContextMenuOpen,
	containerWidth,
	setIsContextMenuOpen,
	setIsFocused,
	setIsHovered,
	id,
	cloudId,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
}: SearchResultContextMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	const sainFrontendExperimentName = useMemo(
		() =>
			FeatureGates.getLayer('kd_sain_layer', {
				fireLayerExposure: false,
			}).get('frontendExperimentName', ''),
		[],
	);
	const serveSummarizeInChatDesignFixExperiment = useMemo(
		() =>
			sainFrontendExperimentName === 'kd_summarize_design_fix_exp' &&
			FeatureGates.getExperimentValue<string>(sainFrontendExperimentName, 'cohort', 'control') ===
				'test',
		[sainFrontendExperimentName],
	);

	const serveSummarizeDesignFix =
		fg('kd_summarize_design_fix') || serveSummarizeInChatDesignFixExperiment;

	const divRef = useRef<HTMLDivElement>(null);

	const copyToClipboard = async () => {
		setIsHovered(false);
		setIsFocused(false);
		if (!url) {
			return;
		}
		await navigator.clipboard.writeText(url);
		onContextMenuCopyButtonClicked?.();
	};

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, onClick, ...props }) => (
				<IconButton
					{...props}
					appearance="subtle"
					icon={MoreIcon}
					label={formatMessage(messages.moreActionsLabel)}
					isTooltipDisabled={false}
					spacing="compact"
					ref={triggerRef}
					onClick={(e) => {
						onClick?.(e);
						onContextMenuButtonClicked?.();
					}}
				/>
			)}
			placement="right-start"
			testId="search-result-context-menu"
			onOpenChange={() => setIsContextMenuOpen(!isContextMenuOpen)}
			isOpen={isContextMenuOpen}
			returnFocusRef={divRef}
			zIndex={2}
		>
			<DropdownItemGroup testId="search-result-context-menu-dropdown">
				{!serveSummarizeDesignFix && (
					<Box onMouseDown={copyToClipboard}>
						<DropdownItem
							elemBefore={
								<LinkIcon
									color="currentColor"
									label="copy-link-icon"
									LEGACY_size="medium"
									spacing="spacious"
								/>
							}
							testId="copy-link-button"
						>
							{formatMessage(messages.copyLinkLabel)}
						</DropdownItem>
					</Box>
				)}
				{containerWidth < SUMMARIZE_IN_CHAT_CONTAINER_WIDTH_BREAKPOINT && !hideSummarizeInChat && (
					<Box
						onMouseDown={() => {
							setIsContextMenuOpen(false);
							onSummarizeInChatButtonClicked?.();

							if (!url) {
								return;
							}
							publish({
								type: 'browser-context-payload',
								source: 'search-result',
								data: {
									context: {
										browserUrl: url,
									},
								},
							});
							publish({
								type: 'chat-new',
								source: 'search-result',
								data: {
									dialogues: [],
									prompt: `Summarize ${url}`,
									name: `Summarize ${url}`,
								},
							});
						}}
					>
						<DropdownItem
							elemBefore={
								<RovoChatIcon
									color="currentColor"
									label="summarize-in-chat-button"
									LEGACY_size="medium"
									spacing="spacious"
								/>
							}
							testId="context-menu-summarize-in-chat-button"
						>
							{formatMessage(messages.summarizeInChatLabel)}
						</DropdownItem>
					</Box>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export default SearchResultContextMenu;
